/* eslint-disable complexity */
import { useRef, useEffect, useState } from 'react'
import { useButton } from 'react-aria'
import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import { LikeButton } from '~/components/elements/LikeButton'
import { ArrowLeftIcon } from '~/components/icons/generated/ArrowLeftIcon'
import { ArrowRightIcon } from '~/components/icons/generated/ArrowRightIcon'
import { getCoordinateImageAlt } from '~/features/shared/coordinate/lib/getCoordinateImageAlt'
import { useCarousel } from '~/hooks/useCarousel'
import { getColorFromColorGroupId } from '~/lib/color'
import { getSeasonFromDate } from '~/lib/dateUtil'
import { sexToGender } from '~/lib/gender/converter'
import { genderToGenderKana } from '~/lib/gender/label'
import { useCoordinateLike } from '../../hooks/useCoordinateLike'
import { sendGtmEvCoordinateImageSwipe } from '../../lib/gtmEvent'
import { ImageCountIndicator } from './ImageCountIndicator'
import { ImageCounter } from './ImageCounter'
import { MainImage } from './MainImage'

type Props = {
  coordinate: Schemas.coordinate_large_with_member_shop_brands
  coordinateItems: Schemas.coordinate_item_resource[]
}

export const CoordinateImages: React.FC<Props> = ({
  coordinate,
  coordinateItems,
}) => {
  const {
    currentIndex: currentImageIndex,
    goFirst: onClickFirst,
    goLast: onClickLast,
    go: onClickThumbnail,
    next: onClickNext,
    prev: onClickPrev,
    ref: imagesContainerRef,
  } = useCarousel<HTMLDivElement>()
  const isFirstImage = currentImageIndex === 0
  const totalImageLength = coordinate.sub_images.length + 1
  const isLastImage = currentImageIndex === totalImageLength
  const hasSubImage = totalImageLength > 1
  const [lastImageIndex, setLastImageIndex] = useState(currentImageIndex)

  // 画像が右にスワイプされたタイミングでGAの処理を行う
  useEffect(() => {
    setLastImageIndex(currentImageIndex)
    if (currentImageIndex <= lastImageIndex) {
      return
    }
    sendGtmEvCoordinateImageSwipe(`${coordinate.id}_no${currentImageIndex + 1}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageIndex])

  const { likeCount, isLiked, toggleLike } = useCoordinateLike(
    coordinate.id,
    coordinate.aggregate?.like_count
  )

  const prevButtonRef = useRef<HTMLButtonElement>(null)
  const { buttonProps: prevButtonProps } = useButton(
    {
      onPress: isFirstImage ? onClickLast : onClickPrev,
      'aria-label': '前のスライドに移動',
    },
    prevButtonRef
  )

  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const { buttonProps: nextButtonProps } = useButton(
    {
      onPress: isLastImage ? onClickFirst : onClickNext,
      'aria-label': '次のスライドに移動',
    },
    nextButtonRef
  )

  const item = coordinateItems[0]

  return (
    <article className="relative">
      <div
        ref={imagesContainerRef}
        className="relative flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden hide-scroll"
      >
        <MainImage coordinate={coordinate} coordinateItems={coordinateItems} />
        {coordinate.sub_images?.map((image, i) => {
          return (
            <section
              key={image.url_1000}
              className="relative flex w-full shrink-0 snap-center"
            >
              <Img
                className="w-full"
                src={image.url_1000}
                alt={`${getCoordinateImageAlt({
                  nickName: coordinate.member.nick_name,
                  brandName: coordinateItems[0]?.item.brand?.name ?? undefined,
                  itemName: coordinateItems[0]?.name,
                  categoryName:
                    item?.item.item_child_category?.name ??
                    item?.item.item_category?.name,
                  colorName: getColorFromColorGroupId(
                    item?.color_group?.id ?? 0
                  ),
                  seasonName: coordinate.published_at
                    ? getSeasonFromDate(coordinate.published_at)
                    : undefined,
                  genderName: genderToGenderKana(
                    sexToGender(coordinate.sex_id)
                  ),
                  height: coordinate.height ?? undefined,
                  bodyFrameTypeName: coordinate.body_frame_type?.name
                    ? `骨格${coordinate.body_frame_type?.name}`
                    : undefined,
                  tagNames: coordinate.tags?.map((tag) => tag.name),
                })}の${i + 2}枚目の写真`}
                width={1000}
                height={1334}
              />
            </section>
          )
        })}
      </div>
      {hasSubImage && (
        <>
          <button
            className="absolute left-[10px] top-1/2 hidden size-[45px] -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-[18px] text-white xl:flex"
            type="button"
            {...prevButtonProps}
            ref={prevButtonRef}
          >
            <ArrowLeftIcon className="mt-[-1px] h-[18px]" />
          </button>
          <button
            className="absolute right-[10px] top-1/2 hidden size-[45px] -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-[18px] text-white xl:flex"
            type="button"
            {...nextButtonProps}
            ref={nextButtonRef}
          >
            <ArrowRightIcon className="mt-[-1px] h-[18px]" />
          </button>
          <div className="absolute right-4 top-4 xl:hidden">
            <ImageCounter
              current={currentImageIndex + 1}
              total={totalImageLength}
            />
          </div>
          <div className="absolute bottom-[15px] flex w-full justify-center xl:hidden">
            <ImageCountIndicator
              current={currentImageIndex + 1}
              total={totalImageLength}
            />
          </div>
        </>
      )}
      <div className="absolute bottom-[-30px] right-4 xl:hidden">
        <LikeButton
          onClick={toggleLike}
          likeCount={likeCount}
          isLiked={isLiked}
        />
      </div>
      {hasSubImage && (
        <ul className="hidden flex-wrap gap-[7px] bg-white px-[23px] py-6 xl:flex">
          {[coordinate.image, ...coordinate.sub_images].map((image, index) => (
            <li key={index}>
              <button
                className="relative flex h-12 w-10 overflow-hidden xl:hover:opacity-70 xl:hover:disabled:opacity-100"
                disabled={index === currentImageIndex}
                onClick={() => onClickThumbnail(index)}
                type="button"
                aria-label={`${index + 1}枚目のコーディネート写真を見る`}
              >
                <Img src={image.url_70} width="40" height="53" alt="" />
                {index === currentImageIndex && (
                  <span className="absolute inset-0 border-2 border-blue-500" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  )
}
